import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { PageHeader } from "../../../components/ui";
import { listBaseRawMaterials, previewRawMaterial, receiveRawMaterial } from "../../../api/rawMaterials";
import { getMaterialVariantDimensions } from "../../../api/materials";
import MaterialVariantSelectModal from "../../../components/MaterialVariantSelectModal";

function useDebouncedValue(value, delayMs) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return v;
}

export default function ShrinkIntakePage() {
  const [baseMaterials, setBaseMaterials] = useState([]);
  const [baseLoading, setBaseLoading] = useState(false);

  const [materialId, setMaterialId] = useState("");
  const [templateDimensions, setTemplateDimensions] = useState([]);
  const [variantModalOpen, setVariantModalOpen] = useState(false);
  const [variant, setVariant] = useState(null);

  const [qty, setQty] = useState("");
  const [note, setNote] = useState("");
  const [unitCost, setUnitCost] = useState("");
  const [currency, setCurrency] = useState("TRY");
  const [priceUnit, setPriceUnit] = useState("");

  const [preview, setPreview] = useState({ stock_code: "", unit: "" });
  const [previewError, setPreviewError] = useState("");
  const [saving, setSaving] = useState(false);
  const lastPreviewReq = useRef(0);

  useEffect(() => {
    let cancelled = false;
    async function loadBase() {
      setBaseLoading(true);
      try {
        const items = await listBaseRawMaterials("shrink");
        if (!cancelled) setBaseMaterials(Array.isArray(items) ? items : []);
      } catch (e) {
        console.error("BASE SHRINK MATERIALS LOAD ERROR", e);
        if (!cancelled) setBaseMaterials([]);
      } finally {
        if (!cancelled) setBaseLoading(false);
      }
    }
    loadBase();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function loadTemplate() {
      try {
        if (!materialId) {
          setTemplateDimensions([]);
          setVariant(null);
          return;
        }
        const items = await getMaterialVariantDimensions(Number(materialId));
        if (!cancelled) setTemplateDimensions(Array.isArray(items) ? items : []);
      } catch (e) {
        console.error("SHRINK TEMPLATE LOAD ERROR", e);
        if (!cancelled) setTemplateDimensions([]);
      }
    }
    loadTemplate();
    return () => {
      cancelled = true;
    };
  }, [materialId]);

  const previewPayload = useMemo(
    () => ({
      material_id: materialId ? Number(materialId) : null,
      selections: variant?.selections || [],
    }),
    [materialId, variant]
  );
  const debounced = useDebouncedValue(previewPayload, 250);

  useEffect(() => {
    if (!debounced.material_id) {
      setPreview({ stock_code: "", unit: "" });
      setPreviewError("");
      return;
    }
    if (templateDimensions.length && !(debounced.selections || []).length) {
      setPreview({ stock_code: "", unit: "" });
      setPreviewError("");
      return;
    }

    const reqId = Date.now();
    lastPreviewReq.current = reqId;
    setPreviewError("");

    previewRawMaterial("shrink", debounced)
      .then((d) => {
        if (lastPreviewReq.current !== reqId) return;
        setPreview({ stock_code: d.stock_code || "", unit: d.unit || "" });
      })
      .catch((err) => {
        if (lastPreviewReq.current !== reqId) return;
        console.error("SHRINK PREVIEW ERROR", err);
        setPreview({ stock_code: "", unit: "" });
        setPreviewError(err?.response?.data?.error?.message || "Kod önizlemesi alınamadı");
      });
  }, [debounced, templateDimensions.length]);

  const selectedMaterial = useMemo(
    () => (materialId ? baseMaterials.find((m) => String(m.id) === String(materialId)) : null),
    [baseMaterials, materialId]
  );

  async function onSave() {
    const q = Number(qty);
    const hasTemplate = templateDimensions.length > 0;

    if (!materialId) {
      alert("Base shrink malzemesi seçin");
      return;
    }
    if (hasTemplate && !(variant?.selections || []).length) {
      alert("Varyant seçin");
      return;
    }
    if (!Number.isFinite(q) || q <= 0) {
      alert("Miktar pozitif olmalı");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        material_id: Number(materialId),
        selections: variant?.selections || [],
        qty: q,
        note: note.trim() ? note.trim() : null,
        unit_cost: unitCost !== "" ? Number(unitCost) : null,
        currency: currency || "TRY",
        price_unit: priceUnit || null,
      };
      const d = await receiveRawMaterial("shrink", payload);
      alert(`Kaydedildi.\n\nKod: ${d.stock_code_preview}\nLot: ${d.lot_no}`);
      setQty("");
      setNote("");
      setUnitCost("");
      setCurrency("TRY");
      setPriceUnit("");
    } catch (err) {
      console.error("SHRINK RECEIVE ERROR", err);
      alert(err?.response?.data?.error?.message || "Kaydetme başarısız");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Stack spacing={2.5} sx={{ maxWidth: 1200, mx: "auto", "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fff" } }}>
      <PageHeader title="Hammadde Giriş — Shrink" subtitle="Base malzeme seçin, (varsa) varyantı seçin, kaydedin." />
      {baseLoading ? <LinearProgress /> : null}

      <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 4, border: "1px solid #a5b4fc", bgcolor: "#f5f3ff", boxShadow: "0 10px 30px rgba(79, 70, 229, 0.10)" }}>
        <Stack spacing={0.5}>
          <Typography variant="overline" sx={{ color: "rgba(15,23,42,0.70)" }}>
            Stok Kodu Önizleme
          </Typography>
          <Typography sx={{ fontSize: { xs: 22, md: 30 }, fontWeight: 900, color: "#312e81", letterSpacing: 0.3 }}>
            {preview.stock_code || "—"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Birim: <b>{preview.unit || "—"}</b>
          </Typography>
          {previewError ? <Alert severity="warning">{previewError}</Alert> : null}
        </Stack>
      </Paper>

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: 4, bgcolor: "#f5f3ff", border: "1px solid #a5b4fc", boxShadow: "0 10px 30px rgba(79, 70, 229, 0.10)" }}>
            <Typography variant="h6" sx={{ fontWeight: 900, mb: 1.5, color: "#312e81" }}>
              Shrink Bilgileri
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel id="shrink-base-label">Base Malzeme</InputLabel>
                  <Select
                    labelId="shrink-base-label"
                    label="Base Malzeme"
                    value={materialId}
                    onChange={(e) => {
                      setMaterialId(String(e.target.value));
                      setVariant(null);
                    }}
                  >
                    <MenuItem value="">
                      <em>Seçiniz</em>
                    </MenuItem>
                    {baseMaterials.map((m) => (
                      <MenuItem key={m.id} value={String(m.id)}>
                        {(m.internal_code ? `${m.internal_code} — ` : "") + (m.name || m.code)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }} justifyContent="space-between">
                  <Stack spacing={0.25}>
                    <Typography sx={{ fontWeight: 900 }}>Varyant</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {variant?.display_name
                        ? variant.display_name
                        : templateDimensions.length
                          ? "Seçilmedi"
                          : "Bu malzemede varyant yok"}
                    </Typography>
                  </Stack>
                  <Button
                    variant="outlined"
                    disabled={!materialId || !templateDimensions.length}
                    onClick={() => setVariantModalOpen(true)}
                    sx={{ borderRadius: 2, textTransform: "none", fontWeight: 800, borderColor: "#818cf8", color: "#4338ca", "&:hover": { borderColor: "#4f46e5", bgcolor: "#eef2ff" } }}
                  >
                    Varyant Seç
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={5}>
          <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: 4, bgcolor: "#f5f3ff", border: "1px solid #a5b4fc", boxShadow: "0 10px 30px rgba(79, 70, 229, 0.10)" }}>
            <Typography variant="h6" sx={{ fontWeight: 900, mb: 1.5, color: "#312e81" }}>
              Stok Girişi
            </Typography>
            <Stack spacing={2}>
              <TextField size="small" label={`Miktar (${selectedMaterial?.unit || "—"})`} value={qty} onChange={(e) => setQty(e.target.value)} />
              <TextField size="small" label="Not (ops.)" value={note} onChange={(e) => setNote(e.target.value)} />
              <Stack direction="row" spacing={1}>
                <TextField size="small" label="Birim Fiyat" value={unitCost} onChange={(e) => setUnitCost(e.target.value)} inputMode="decimal" sx={{ flex: 2 }} />
                <FormControl size="small" sx={{ flex: 1, minWidth: 90 }}>
                  <InputLabel>Fiyatın Birimi</InputLabel>
                  <Select value={priceUnit} label="Fiyatın Birimi" onChange={(e) => setPriceUnit(e.target.value)}>
                    <MenuItem value=""><em>—</em></MenuItem>
                    <MenuItem value="kg">kg</MenuItem>
                    <MenuItem value="adet">adet</MenuItem>
                    <MenuItem value="m">m</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ flex: 1, minWidth: 90 }}>
                  <InputLabel>Döviz</InputLabel>
                  <Select value={currency} label="Döviz" onChange={(e) => setCurrency(e.target.value)}>
                    <MenuItem value="TRY">TRY</MenuItem>
                    <MenuItem value="USD">USD</MenuItem>
                    <MenuItem value="EUR">EUR</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
              <Button variant="contained" fullWidth disabled={saving || baseLoading} onClick={onSave} sx={{ mt: 0.5, py: 1.2, borderRadius: 2.25, textTransform: "none", fontWeight: 800, bgcolor: "#4f46e5", "&:hover": { bgcolor: "#3730a3" } }}>
                {saving ? "Kaydediliyor..." : "Kaydet"}
              </Button>
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {variantModalOpen ? (
        <MaterialVariantSelectModal
          materialId={Number(materialId)}
          materialName={selectedMaterial?.name || ""}
          templateDimensions={templateDimensions}
          onClose={() => setVariantModalOpen(false)}
          onResolved={(v) => setVariant(v)}
        />
      ) : null}
    </Stack>
  );
}

